// Drawer admin para editar el contenido de una lección: vocab, speaking blocks y activities.
// Cada subtabla se guarda con un PUT independiente (idempotente — replace all).

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import {
  adminReplaceActivities,
  adminReplaceSpeaking,
  adminReplaceVocab,
  type LessonActivity,
  type SpeakingBlock,
  type SpeakingMediaType,
  type VocabItem,
} from '@/services/lessons';
import { adminCreateQuiz, adminListQuizzes, type QuizMeta } from '@/services/quizzes';
import { QuizEditorDrawer } from '@/components/organisms/QuizEditorDrawer';
import styles from './LessonContentDrawer.module.scss';

interface LessonContentDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: number;
  initialVocab: VocabItem[];
  initialSpeaking: SpeakingBlock[];
  initialActivities: LessonActivity[];
  onSaved: () => void;
}

type TabKey = 'vocab' | 'speaking' | 'activities';

function emptyVocab(): VocabItem[] {
  return [1, 2, 3].map((p) => ({
    position: p, word: null, phonetic: null,
    phonetic_image_url: null, learn_text: null, learn_image_url: null,
  }));
}

function emptySpeaking(): SpeakingBlock[] {
  return [1, 2, 3, 4].map((i) => ({
    block_index: i, title: null, content: null, media_type: 'none' as SpeakingMediaType, media_url: null,
  }));
}

function emptyActivities(): LessonActivity[] {
  return [1, 2, 3].map((p) => ({
    position: p, enunciado: null, quiz_id: null,
  }));
}

function mergeVocab(existing: VocabItem[]): VocabItem[] {
  const out = emptyVocab();
  for (const it of existing) out[it.position - 1] = { ...out[it.position - 1]!, ...it };
  return out;
}
function mergeSpeaking(existing: SpeakingBlock[]): SpeakingBlock[] {
  const out = emptySpeaking();
  for (const b of existing) out[b.block_index - 1] = { ...out[b.block_index - 1]!, ...b };
  return out;
}
function mergeActivities(existing: LessonActivity[]): LessonActivity[] {
  const out = emptyActivities();
  for (const a of existing) out[a.position - 1] = { ...out[a.position - 1]!, ...a };
  return out;
}

export function LessonContentDrawer({
  open, onOpenChange, lessonId,
  initialVocab, initialSpeaking, initialActivities,
  onSaved,
}: LessonContentDrawerProps) {
  const [tab, setTab] = useState<TabKey>('vocab');
  const [vocab, setVocab] = useState<VocabItem[]>(emptyVocab());
  const [speaking, setSpeaking] = useState<SpeakingBlock[]>(emptySpeaking());
  const [activities, setActivities] = useState<LessonActivity[]>(emptyActivities());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quizzes, setQuizzes] = useState<QuizMeta[]>([]);
  const [editingQuizId, setEditingQuizId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setVocab(mergeVocab(initialVocab));
    setSpeaking(mergeSpeaking(initialSpeaking));
    setActivities(mergeActivities(initialActivities));
    setError(null);
    setTab('vocab');
    void adminListQuizzes().then(setQuizzes).catch(() => setQuizzes([]));
  }, [open, initialVocab, initialSpeaking, initialActivities]);

  async function reloadQuizzes() {
    try {
      const items = await adminListQuizzes();
      setQuizzes(items);
    } catch {
      // silencioso
    }
  }

  async function createQuizForActivity(idx: number) {
    setBusy(true);
    setError(null);
    try {
      const newId = await adminCreateQuiz(`Quiz ${Date.now()}`);
      await reloadQuizzes();
      setActivities((prev) => prev.map((a, i) => (i === idx ? { ...a, quiz_id: newId } : a)));
      setEditingQuizId(newId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al crear quiz');
    } finally {
      setBusy(false);
    }
  }

  function setVocabField(idx: number, key: keyof VocabItem, value: string | null) {
    setVocab((prev) => prev.map((it, i) => i === idx ? { ...it, [key]: value || null } : it));
  }
  function setSpeakingField(idx: number, key: keyof SpeakingBlock, value: string | null) {
    setSpeaking((prev) => prev.map((b, i) => i === idx ? { ...b, [key]: value || null } : b));
  }
  function setActivityField(idx: number, key: keyof LessonActivity, value: string | null) {
    setActivities((prev) => prev.map((a, i) => i === idx ? {
      ...a,
      [key]: key === 'quiz_id'
        ? (value && /^\d+$/.test(value) ? Number(value) : null)
        : (value || null),
    } : a));
  }

  // Filtra slots que tienen contenido real (al menos un campo no vacío relevante).
  function nonEmptyVocab(): VocabItem[] {
    return vocab.filter((it) => it.word || it.phonetic || it.learn_text || it.phonetic_image_url || it.learn_image_url);
  }
  function nonEmptySpeaking(): SpeakingBlock[] {
    return speaking.filter((b) => b.title || b.content || (b.media_type !== 'none' && b.media_url));
  }
  function nonEmptyActivities(): LessonActivity[] {
    return activities.filter((a) => a.enunciado || a.quiz_id !== null);
  }

  async function saveTab() {
    setBusy(true);
    setError(null);
    try {
      if (tab === 'vocab')      await adminReplaceVocab(lessonId, nonEmptyVocab());
      if (tab === 'speaking')   await adminReplaceSpeaking(lessonId, nonEmptySpeaking());
      if (tab === 'activities') await adminReplaceActivities(lessonId, nonEmptyActivities());
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>Editar contenido de la lección</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.tabs} role="tablist">
            <button role="tab" aria-selected={tab==='vocab'}      className={`${styles.tab} ${tab==='vocab'?styles.tabActive:''}`}      onClick={() => setTab('vocab')}>Vocabulario</button>
            <button role="tab" aria-selected={tab==='speaking'}   className={`${styles.tab} ${tab==='speaking'?styles.tabActive:''}`}   onClick={() => setTab('speaking')}>Speaking</button>
            <button role="tab" aria-selected={tab==='activities'} className={`${styles.tab} ${tab==='activities'?styles.tabActive:''}`} onClick={() => setTab('activities')}>Activities</button>
          </div>

          <div className={styles.body}>
            {tab === 'vocab' && vocab.map((it, idx) => (
              <fieldset key={idx} className={styles.fieldset}>
                <legend>Vocabulario {it.position}</legend>
                <div className={styles.row2}>
                  <label className={styles.field}>
                    <span>Palabra</span>
                    <input value={it.word ?? ''} onChange={(e) => setVocabField(idx, 'word', e.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>Fonética</span>
                    <input value={it.phonetic ?? ''} onChange={(e) => setVocabField(idx, 'phonetic', e.target.value)} placeholder="/tɜːn/" />
                  </label>
                </div>
                <label className={styles.field}>
                  <span>Imagen fonética (URL)</span>
                  <input value={it.phonetic_image_url ?? ''} onChange={(e) => setVocabField(idx, 'phonetic_image_url', e.target.value)} placeholder="https://..." />
                </label>
                <label className={styles.field}>
                  <span>Texto de aprendizaje</span>
                  <textarea rows={2} value={it.learn_text ?? ''} onChange={(e) => setVocabField(idx, 'learn_text', e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Imagen de aprendizaje (URL)</span>
                  <input value={it.learn_image_url ?? ''} onChange={(e) => setVocabField(idx, 'learn_image_url', e.target.value)} placeholder="https://..." />
                </label>
              </fieldset>
            ))}

            {tab === 'speaking' && speaking.map((b, idx) => (
              <fieldset key={idx} className={styles.fieldset}>
                <legend>Speaking {b.block_index}</legend>
                <label className={styles.field}>
                  <span>Título</span>
                  <input value={b.title ?? ''} onChange={(e) => setSpeakingField(idx, 'title', e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Contenido / instrucción</span>
                  <textarea rows={3} value={b.content ?? ''} onChange={(e) => setSpeakingField(idx, 'content', e.target.value)} />
                </label>
                <div className={styles.row2}>
                  <label className={styles.field}>
                    <span>Tipo de media</span>
                    <select value={b.media_type} onChange={(e) => setSpeakingField(idx, 'media_type', e.target.value)}>
                      <option value="none">Sin media</option>
                      <option value="image">Imagen</option>
                      <option value="video">Video</option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    <span>URL del media</span>
                    <input value={b.media_url ?? ''} onChange={(e) => setSpeakingField(idx, 'media_url', e.target.value)} placeholder="https://..." disabled={b.media_type === 'none'} />
                  </label>
                </div>
              </fieldset>
            ))}

            {tab === 'activities' && activities.map((a, idx) => (
              <fieldset key={idx} className={styles.fieldset}>
                <legend>Activity {a.position}</legend>
                <label className={styles.field}>
                  <span>Enunciado</span>
                  <input value={a.enunciado ?? ''} onChange={(e) => setActivityField(idx, 'enunciado', e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Quiz vinculado</span>
                  <select
                    value={a.quiz_id ?? ''}
                    onChange={(e) => setActivityField(idx, 'quiz_id', e.target.value)}
                  >
                    <option value="">— sin quiz —</option>
                    {quizzes.map((q) => (
                      <option key={q.id} value={q.id}>{q.id} · {q.title}</option>
                    ))}
                  </select>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {a.quiz_id ? (
                    <Button variant="secondary" size="sm" onClick={() => setEditingQuizId(a.quiz_id)}>
                      <Icon name="book" size={12} /> Editar quiz
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => void createQuizForActivity(idx)} disabled={busy}>
                      <Icon name="plus" size={12} /> Crear quiz nuevo
                    </Button>
                  )}
                </div>
              </fieldset>
            ))}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <footer className={styles.footer}>
            <span className={styles.muted}>Guardar reemplaza todo el contenido de esta sección. Los slots vacíos se eliminan.</span>
            <Button variant="primary" disabled={busy} onClick={() => void saveTab()}>
              {busy ? 'Guardando…' : `Guardar ${tab === 'vocab' ? 'vocabulario' : tab === 'speaking' ? 'speaking' : 'activities'}`}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>

      <QuizEditorDrawer
        open={editingQuizId !== null}
        onOpenChange={(o) => { if (!o) setEditingQuizId(null); }}
        quizId={editingQuizId}
        onSaved={() => { void reloadQuizzes(); setEditingQuizId(null); }}
      />
    </Dialog.Root>
  );
}
